import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import CustomTooltip from "src/components/visibility/components/CustomTooltip";
import { getCurrencySymbol } from "src/utils/get-currency";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";

type Props = {
    rows: any[];
    onChange?: (rows: any[]) => void;
    mode: string;
    errorCellId?: string | null;
    validationErrors?: Record<string, string[]>;
};

export function getKoBiddingRules(row: any) {
    const platform = row.platform;
    const type = row.bidding_strategy_type;

    const isGoogle = platform === "Google Ads";
    const isBing = platform === "Bing Ads";

    const isPortfolio =
        [
            "Target ROAS",
            "Maximize Conversion Value",
            "Target CPA",
            "Maximize Conversions",
        ].includes(type);

    const isCampaignLevel = type === "Target Impression Share";

    return {
        isGoogle,
        isBing,
        isPortfolio,
        isCampaignLevel,

        controlLayer: isGoogle && isPortfolio ? "portfolio" : "campaign",

        minCPC: isGoogle ? 0.01 : 0.05,

        editable: {
            roas:
                type === "Target ROAS" ||
                type === "Maximize Conversion Value",

            cpa:
                type === "Target CPA" ||
                type === "Maximize Conversions",

            maxCpc: true,

            impressionShare: isCampaignLevel,

            position: isCampaignLevel,
        },
    };
}

export default function DataGridCreateStrategyCampaigns({ rows, onChange, mode, errorCellId, validationErrors = {} }: Props) {
    const workspace: any = useCurrentWorkspace();
    const lang = localStorage.getItem('i18nextLng') || 'en';

    const baseColumns: GridColDef[] = [
        {
            field: "platform",
            type: "string",
            renderHeader: () => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography fontWeight={600} color="rgba(99, 115, 129, 1)" fontSize={14}>
                        Platform
                    </Typography>
                </Box>
            ),
            disableColumnMenu: true,
            headerName: "Platform",
            maxWidth: 100,
            sortable: true,
            filterable: true,
            renderCell: (params: any) => {
                const value = params.value || "";

                const formatted =
                    value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

                return (
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            color: "rgba(51, 60, 71, 1)",
                            fontWeight: "normal"
                        }}
                    >
                        {formatted}
                    </Box>
                );
            }
        },
        {
            field: "account_name", headerName: "Account", flex: 1, minWidth: 180,
            renderCell: (params: any) => {
                return (
                    <Typography fontSize={14} color={"rgba(51, 60, 71, 1)"}>{params?.value}</Typography>
                )
            }
        },
        {
            field: "campaign_name",
            sortable: true,
            disableColumnMenu: true,
            renderHeader: (params: any) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Campaign
                    </Typography>

                </Box>
            ),
            headerName: "Campaign",
            flex: 1,
            maxWidth: 250,
            renderCell: ({ value, row }: any) => {
                const values = Array.isArray(value) ? value : value ? [value] : [];

                return (
                    <CustomTooltip title={values[0] || ""} placement="top" arrow>
                        <Box
                            sx={{ "&:hover": { cursor: "pointer" } }}
                            display="flex"
                            alignItems="center"
                            px="12px"
                            overflow="hidden"
                            minWidth={0}
                            width="100%"
                        >
                            {values.map((c: string, i: number) => (
                                <Box
                                    key={i}
                                    display="flex"
                                    alignItems="center"
                                    overflow="hidden"
                                    minWidth={0}
                                    width={"100%"}
                                    justifyContent={"space-between"}
                                >
                                    <Box display={"flex"} flexDirection={"row"}>
                                        <Box
                                            component="span"
                                            sx={{
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                display: "block",
                                                flex: 1,
                                                minWidth: 0,
                                                maxWidth: 340,
                                                mt: .35,
                                                color: "rgba(51, 60, 71, 1)",
                                            }}
                                        >
                                            {c}
                                        </Box>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </CustomTooltip>
                );
            },
        },
        {
            field: "bidding_strategy_type",
            type: 'string',
            renderHeader: (params: any) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Bidding Strategy Type
                    </Typography>
                </Box>
            ),
            renderCell: (params: any) => (
                <Typography
                    sx={{
                        color: "rgba(51, 60, 71, 1)",
                        fontSize: 14,
                    }}
                >
                    {params.value}
                </Typography>
            ),
            disableColumnMenu: true,
            headerName: "Bidding Strategy Type",
            maxWidth: 200,
            flex: 1,
            sortable: true,
            filterable: true,
        },

    ];

    const columns = [
        ...baseColumns,
        ...(mode === "KO Bidding Manual"
            ? [
                {
                    field: "target_roas",
                    headerName: "Target ROAS",
                    type: "number",
                    editable: true,
                    maxWidth: 180,
                    flex: 1,
                    renderCell: (params: any) => {
                        const isError = params.value != null && params.value < 0;
                        const rules = getKoBiddingRules(params.row);

                        const isRequiredError =
                            validationErrors?.[params.row.id]?.includes("target_roas");

                        return (
                            <Box
                                onClick={() => {
                                    params.api.startCellEditMode({
                                        id: params.id,
                                        field: params.field,
                                    });
                                }}
                                sx={{
                                    borderRadius: "8px",
                                    border: `2px solid ${isError || isRequiredError
                                        ? "red"
                                        : "rgba(145, 158, 171, 0.16)"
                                        }`,
                                    display: "flex",
                                    alignItems: "center",
                                    width: "90px",
                                    height: "36px",
                                    overflow: "hidden",
                                    backgroundColor: isError ? "rgba(255,0,0,0.05)" : "transparent",
                                    "&:hover": {
                                        cursor: "pointer",
                                        borderColor: isError ? "red" : "rgba(0, 108, 156, 0.40)",
                                        backgroundColor: isError
                                            ? "rgba(255,0,0,0.05)"
                                            : "rgba(0, 108, 156, 0.03)",
                                        boxShadow: isError
                                            ? "0 0 0 2px rgba(255,0,0,0.2)"
                                            : "0 0 0 2px rgba(51, 138, 243, 0.20)",
                                    },
                                    opacity: rules.editable.roas ? 1 : 0.4
                                }}
                            >
                                <TextField
                                    value={params.value ?? ""}
                                    type="number"
                                    disabled={!rules.editable.roas}
                                    variant="standard"
                                    placeholder="--"
                                    onChange={(e: any) => {
                                        const val = e.target.value;

                                        if (val === "") {
                                            params.api.setEditCellValue({
                                                id: params.id,
                                                field: params.field,
                                                value: "",
                                            });
                                            return;
                                        }

                                        const num = Number(val);

                                        if (num >= 0) {
                                            params.api.setEditCellValue({
                                                id: params.id,
                                                field: params.field,
                                                value: num,
                                            });
                                        }
                                    }}
                                    inputProps={{
                                        min: 0,
                                        step: 1,
                                        style: {
                                            textAlign: "right",
                                            padding: "6px 8px",
                                            fontSize: "14px",
                                        },
                                    }}
                                    sx={{
                                        width: "100%",
                                        "& .MuiInput-root:before": { display: "none" },
                                        "& .MuiInput-root:after": { display: "none" },
                                    }}
                                />
                                <Box sx={{ pr: 1, fontSize: 12, color: "rgba(99,115,129,1)" }}>
                                    %
                                </Box>
                            </Box>
                        );
                    }
                },
                {
                    field: "target_cpa",
                    headerName: "Target CPA",
                    type: "number",
                    editable: true,
                    maxWidth: 180,
                    flex: 1,
                    renderCell: (params: any) => {
                        const isError = params.value != null && params.value < 0;
                        const rules = getKoBiddingRules(params.row);
                        const isRequiredError =
                            validationErrors?.[params.row.id]?.includes("target_cpa");
                        return (
                            <Box
                                sx={{
                                    borderRadius: "8px",
                                    border: `2px solid ${isError || isRequiredError
                                        ? "red"
                                        : "rgba(145, 158, 171, 0.16)"
                                        }`,
                                    display: "flex",
                                    alignItems: "center",
                                    width: "90px",
                                    height: "36px",
                                    overflow: "hidden",
                                    backgroundColor: isError ? "rgba(255,0,0,0.05)" : "transparent",
                                    "&:hover": {
                                        cursor: "pointer",
                                        borderColor: isError ? "red" : "rgba(0, 108, 156, 0.40)",
                                        backgroundColor: isError
                                            ? "rgba(255,0,0,0.05)"
                                            : "rgba(0, 108, 156, 0.03)",
                                        boxShadow: isError
                                            ? "0 0 0 2px rgba(255,0,0,0.2)"
                                            : "0 0 0 2px rgba(51, 138, 243, 0.20)",
                                    },
                                    opacity: rules.editable.cpa ? 1 : 0.4,
                                }}
                            >
                                <TextField
                                    value={params.value ?? ""}
                                    type="number"
                                    variant="standard"
                                    disabled={!rules.editable.cpa}
                                    placeholder="--"
                                    onChange={(e: any) => {
                                        const val = e.target.value;

                                        if (val === "") {
                                            params.api.setEditCellValue({
                                                id: params.id,
                                                field: params.field,
                                                value: "",
                                            });
                                            return;
                                        }

                                        const num = Number(val);

                                        if (num >= 0) {
                                            params.api.setEditCellValue({
                                                id: params.id,
                                                field: params.field,
                                                value: num,
                                            });
                                        }
                                    }}
                                    inputProps={{
                                        min: 0,
                                        step: 0.01,
                                        style: {
                                            textAlign: "right",
                                            padding: "6px 8px",
                                            fontSize: "14px",
                                        },
                                    }}
                                    sx={{
                                        width: "100%",
                                        "& .MuiInput-root:before": { display: "none" },
                                        "& .MuiInput-root:after": { display: "none" },
                                    }}
                                />

                                <Box sx={{ pr: 1, fontSize: 12, color: "rgba(99,115,129,1)" }}>
                                    {getCurrencySymbol(workspace?.currency, lang)}
                                </Box>
                            </Box>
                        );
                    }
                },
                {
                    field: "target_impression_share_position",
                    headerName: "Position",
                    flex: 1,
                    editable: true,
                    minWidth: 250,
                    sortable: false,
                    renderCell: (params: any) => {
                        const value = params.value || null;
                        const isRequiredError =
                            validationErrors?.[params.row.id]?.includes(
                                "target_impression_share_position"
                            );
                        return (
                            <Box
                                sx={{
                                    borderRadius: "8px",
                                    border: `2px solid ${isRequiredError
                                        ? "red"
                                        : "rgba(145, 158, 171, 0.16)"
                                        }`,
                                    display: "flex",
                                    alignItems: "center",
                                    width: "100%",
                                    height: "36px",
                                    overflow: "hidden",
                                    "&:hover": {
                                        borderColor: "rgba(0, 108, 156, 0.40)",
                                        backgroundColor: "rgba(0, 108, 156, 0.03)",
                                        boxShadow: "0 0 0 2px rgba(51, 138, 243, 0.20)",
                                        cursor: "pointer",
                                    },
                                    opacity: params.row?.bidding_strategy_type !== "Target Impression Share" ? .4 : 1
                                }}
                            >
                                <Autocomplete
                                    options={["Anywhere on results page", "Top of results page", "Absolute top of results page"]}
                                    value={value}
                                    onChange={(event, newValue) => {
                                        const updatedRows = rows.map((r) =>
                                            r.id === params.id
                                                ? { ...r, target_impression_share_position: newValue ?? "" }
                                                : r
                                        );

                                        onChange?.(updatedRows);
                                    }}
                                    disabled={params.row?.bidding_strategy_type !== "Target Impression Share"}
                                    disableClearable
                                    renderInput={(paramsInput) => (
                                        <TextField
                                            {...paramsInput}
                                            variant="standard"
                                            placeholder="--"
                                            sx={{
                                                px: 1,
                                                width: "100%",
                                                "& .MuiInput-root:before": { display: "none" },
                                                "& .MuiInput-root:after": { display: "none" },
                                                "& input": {
                                                    padding: "6px 8px",
                                                    fontSize: "14px",
                                                    color: "rgba(51, 60, 71, 1)",
                                                },
                                            }}
                                        />
                                    )}
                                    sx={{
                                        width: "100%",
                                        "& .MuiAutocomplete-inputRoot": {
                                            padding: "0 !important",
                                        },
                                    }}
                                />
                            </Box>
                        );
                    }
                },
                {
                    field: "target_impression_share",
                    headerName: "Target IS",
                    type: "number",
                    editable: true,
                    maxWidth: 180,
                    flex: 1,
                    renderCell: (params: any) => {
                        const rules = getKoBiddingRules(params.row);
                        const isError = params.value != null && params.value < 0;

                        const isRequiredError =
                            validationErrors?.[params.row.id]?.includes(
                                "target_impression_share"
                            );
                        return (
                            <Box
                                onClick={() => {
                                    params.api.startCellEditMode({
                                        id: params.id,
                                        field: params.field,
                                    });
                                }}
                                sx={{
                                    borderRadius: "8px",
                                    border: `2px solid ${isError || isRequiredError
                                        ? "red"
                                        : "rgba(145, 158, 171, 0.16)"
                                        }`,
                                    display: "flex",
                                    alignItems: "center",
                                    width: "90px",
                                    height: "36px",
                                    overflow: "hidden",
                                    backgroundColor: isError ? "rgba(255,0,0,0.05)" : "transparent",
                                    "&:hover": {
                                        cursor: "pointer",
                                        borderColor: isError ? "red" : "rgba(0, 108, 156, 0.40)",
                                        backgroundColor: isError
                                            ? "rgba(255,0,0,0.05)"
                                            : "rgba(0, 108, 156, 0.03)",
                                        boxShadow: isError
                                            ? "0 0 0 2px rgba(255,0,0,0.2)"
                                            : "0 0 0 2px rgba(51, 138, 243, 0.20)",
                                    },
                                    opacity: rules.editable.impressionShare ? 1 : 0.4,
                                }}
                            >
                                <TextField
                                    value={params.value ?? ""}
                                    type="number"
                                    variant="standard"
                                    placeholder="--"
                                    disabled={!rules.editable.impressionShare}
                                    onChange={(e: any) => {
                                        const val = e.target.value;

                                        if (val === "") {
                                            params.api.setEditCellValue({
                                                id: params.id,
                                                field: params.field,
                                                value: "",
                                            });
                                            return;
                                        }

                                        const num = Number(val);

                                        if (num >= 0 && num <= 100) {
                                            params.api.setEditCellValue({
                                                id: params.id,
                                                field: params.field,
                                                value: num,
                                            });
                                        }
                                    }}
                                    inputProps={{
                                        min: 0,
                                        max: 100,
                                        step: 1,
                                        style: {
                                            textAlign: "right",
                                            padding: "6px 8px",
                                            fontSize: "14px",
                                        },
                                    }}
                                    sx={{
                                        width: "100%",
                                        "& .MuiInput-root:before": { display: "none" },
                                        "& .MuiInput-root:after": { display: "none" },
                                    }}
                                />

                                <Box sx={{ pr: 1, fontSize: 12, color: "rgba(99,115,129,1)" }}>
                                    %
                                </Box>
                            </Box>
                        );
                    }
                },
                {
                    field: "max_cpc",
                    headerName: "Max CPC",
                    type: "number",
                    editable: true,
                    maxWidth: 180,
                    flex: 1,
                    renderCell: (params: any) => {
                        const rules = getKoBiddingRules(params.row);
                        const isError = params.value != null && params.value < rules.minCPC;
                        const isRequiredError =
                            validationErrors?.[params.row.id]?.includes("max_cpc");
                        return (
                            <Box
                                onClick={() => {
                                    params.api.startCellEditMode({
                                        id: params.id,
                                        field: params.field,
                                    });
                                }}
                                sx={{
                                    borderRadius: "8px",
                                    border: `2px solid ${isError || isRequiredError
                                        ? "red"
                                        : "rgba(145, 158, 171, 0.16)"
                                        }`,
                                    display: "flex",
                                    alignItems: "center",
                                    width: "90px",
                                    height: "36px",
                                    overflow: "hidden",
                                    backgroundColor: isError ? "rgba(255,0,0,0.05)" : "transparent",
                                    "&:hover": {
                                        cursor: "pointer",
                                        borderColor: isError ? "red" : "rgba(0, 108, 156, 0.40)",
                                        backgroundColor: isError
                                            ? "rgba(255,0,0,0.05)"
                                            : "rgba(0, 108, 156, 0.03)",
                                        boxShadow: isError
                                            ? "0 0 0 2px rgba(255,0,0,0.2)"
                                            : "0 0 0 2px rgba(51, 138, 243, 0.20)",
                                    },
                                }}
                            >
                                <TextField
                                    value={params.value ?? ""}
                                    type="number"
                                    variant="standard"
                                    placeholder="--"
                                    onChange={(e: any) => {
                                        const val = e.target.value;

                                        if (val === "") {
                                            params.api.setEditCellValue({
                                                id: params.id,
                                                field: params.field,
                                                value: "",
                                            });
                                            return;
                                        }

                                        const num = Number(val);

                                        if (num >= rules.minCPC) {
                                            params.api.setEditCellValue({
                                                id: params.id,
                                                field: params.field,
                                                value: num,
                                            });
                                        }
                                    }}
                                    inputProps={{
                                        min: 0,
                                        step: 0.01,
                                        style: {
                                            textAlign: "right",
                                            padding: "6px 8px",
                                            fontSize: "14px",
                                        },
                                    }}
                                    sx={{
                                        width: "100%",
                                        "& .MuiInput-root:before": { display: "none" },
                                        "& .MuiInput-root:after": { display: "none" },
                                    }}
                                />

                                <Box sx={{ pr: 1, fontSize: 12, color: "rgba(99,115,129,1)" }}>
                                    {getCurrencySymbol(workspace?.currency, lang)}
                                </Box>
                            </Box>
                        );
                    }
                }
            ]
            : mode === "KO Bidding Auto"
                ? [
                    {
                        field: "target_roas_portfolio_strategy",
                        headerName: "Target ROAS",
                        type: "number",
                        editable: true,
                        maxWidth: 180,
                        flex: 1,
                        renderCell: (params: any) => {
                            const isError = params.value != null && params.value < 0;
                            const rules = getKoBiddingRules(params.row);
                            const isRequiredError =
                                validationErrors?.[params.row.id]?.includes(
                                    "target_roas_portfolio_strategy"
                                );
                            return (
                                <Box
                                    onClick={() => {
                                        params.api.startCellEditMode({
                                            id: params.id,
                                            field: params.field,
                                        });
                                    }}
                                    sx={{
                                        borderRadius: "8px",
                                        border: `2px solid ${isError || isRequiredError
                                            ? "red"
                                            : "rgba(145, 158, 171, 0.16)"
                                            }`,
                                        display: "flex",
                                        alignItems: "center",
                                        width: "90px",
                                        height: "36px",
                                        overflow: "hidden",
                                        backgroundColor: isError
                                            ? "rgba(255,0,0,0.05)"
                                            : "transparent",
                                        "&:hover": {
                                            cursor: "pointer",
                                            borderColor: isError
                                                ? "red"
                                                : "rgba(0, 108, 156, 0.40)",
                                            backgroundColor: isError
                                                ? "rgba(255,0,0,0.05)"
                                                : "rgba(0, 108, 156, 0.03)",
                                            boxShadow: isError
                                                ? "0 0 0 2px rgba(255,0,0,0.2)"
                                                : "0 0 0 2px rgba(51, 138, 243, 0.20)",
                                        },
                                        opacity: rules.editable.roas ? 1 : 0.4,
                                    }}
                                >
                                    <TextField
                                        value={params.value ?? ""}
                                        type="number"
                                        disabled={!rules.editable.roas}
                                        variant="standard"
                                        placeholder="--"
                                        onChange={(e: any) => {
                                            const val = e.target.value;

                                            if (val === "") {
                                                params.api.setEditCellValue({
                                                    id: params.id,
                                                    field: params.field,
                                                    value: "",
                                                });
                                                return;
                                            }

                                            const num = Number(val);

                                            if (num >= 0) {
                                                params.api.setEditCellValue({
                                                    id: params.id,
                                                    field: params.field,
                                                    value: num,
                                                });
                                            }
                                        }}
                                        inputProps={{
                                            min: 0,
                                            step: 1,
                                            style: {
                                                textAlign: "right",
                                                padding: "6px 8px",
                                                fontSize: "14px",
                                            },
                                        }}
                                        sx={{
                                            width: "100%",
                                            "& .MuiInput-root:before": {
                                                display: "none",
                                            },
                                            "& .MuiInput-root:after": {
                                                display: "none",
                                            },
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            pr: 1,
                                            fontSize: 12,
                                            color: "rgba(99,115,129,1)",
                                        }}
                                    >
                                        %
                                    </Box>
                                </Box>
                            );
                        },
                    },
                    {
                        field: "strategy",
                        headerName: "KPI",
                        editable: true,
                        flex: 1,
                        minWidth: 150,
                        type: "singleSelect",
                        valueOptions: [
                            { value: "Smart Top", label: "Smart Top" },
                            { value: "Smart First", label: "Smart First" },
                            { value: "Paid Top", label: "Paid Top" },
                            { value: "Paid First", label: "Paid First" },
                        ],

                        valueGetter: (params: any) => {
                            const detail = params.row.activations_details?.find(
                                (d: any) => d.mode === "KO Bidding Auto"
                            );
                            return detail?.strategy ?? "Smart Top";
                        },

                        valueSetter: (params: any) => {
                            const { value, row } = params;
                            const updatedDetails = [...(row.activations_details || [])];
                            const index = updatedDetails.findIndex((d) => d.mode === "KO Bidding Auto");

                            const selectedValue = value || "Smart Top";

                            if (index >= 0) {
                                updatedDetails[index] = {
                                    ...updatedDetails[index],
                                    strategy: selectedValue
                                };
                            } else {
                                updatedDetails.push({
                                    mode: "KO Bidding Auto",
                                    strategy: selectedValue
                                });
                            }

                            return {
                                ...row,
                                activations_details: updatedDetails,
                                strategy: selectedValue
                            };
                        },


                        renderCell: (params: any) => (
                            <Box
                                onClick={() => {
                                    params.api.startCellEditMode({
                                        id: params.id,
                                        field: params.field,
                                    });
                                }}
                                sx={{
                                    borderRadius: '6px',
                                    border: '1px solid rgba(145, 158, 171, 0.16)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: "100%",
                                    color: "rgba(51, 60, 71, 1)",
                                    justifyContent: 'center',
                                    padding: '6px 8px',
                                    '&:hover': {
                                        cursor: 'pointer',
                                        borderColor: 'rgba(0, 108, 156, 0.40)',
                                        backgroundColor: 'rgba(0, 108, 156, 0.03)',
                                        boxShadow: '0 0 0 2px rgba(51, 138, 243, 0.20)',
                                    }
                                }}
                            >
                                {params.value ?? "--"}
                            </Box>
                        ),
                    },
                    {
                        field: "accepted_variation_threshold",
                        headerName: "Target",
                        type: "number",
                        editable: true,
                        flex: 1,
                        minWidth: 120,

                        valueGetter: (params: any) => {
                            const detail = params.row.activations_details?.find(
                                (d: any) => d.mode === "KO Bidding Auto"
                            );
                            return detail?.accepted_variation_threshold ?? null;
                        },

                        valueSetter: (params: any) => {
                            const { value, row } = params;
                            const updatedDetails = [...(row.activations_details || [])];
                            const index = updatedDetails.findIndex((d) => d.mode === "KO Bidding Auto");
                            const clampedValue = Math.max(1, Math.min(99, Number(value) || 0));

                            if (index >= 0) {
                                updatedDetails[index] = {
                                    ...updatedDetails[index],
                                    accepted_variation_threshold: clampedValue,
                                    strategy: updatedDetails[index].strategy || "Smart Top",
                                };
                            } else {
                                updatedDetails.push({
                                    strategy: "Smart Top",
                                    mode: "KO Bidding Auto",
                                    accepted_variation_threshold: clampedValue
                                });
                            }

                            return {
                                ...row,
                                activations_details: updatedDetails,
                                accepted_variation_threshold: clampedValue,
                                strategy: updatedDetails[index]?.strategy || "Smart Top",

                            };
                        },
                        renderCell: (params: any) => {
                            const isError = params.id === errorCellId;
                            const isRequiredError =
                                validationErrors?.[params.row.id]?.includes(
                                    "accepted_variation_threshold"
                                );
                            return (
                                <Box
                                    onClick={() => {
                                        params.api.startCellEditMode({
                                            id: params.id,
                                            field: params.field,
                                        });
                                    }}
                                    sx={{
                                        borderRadius: '6px',
                                        border: `2px solid ${isError || isRequiredError
                                            ? "red"
                                            : "rgba(145, 158, 171, 0.16)"
                                            }`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: "50%",
                                        cursor: 'pointer',
                                        color: "rgba(51, 60, 71, 1)",
                                        justifyContent: 'center',
                                        padding: '6px 8px',
                                        backgroundColor: isError ? 'rgba(255,0,0,0.05)' : 'transparent',
                                        '&:hover': {
                                            cursor: 'pointer',
                                            borderColor: isError ? 'red' : 'rgba(0, 108, 156, 0.40)',
                                            backgroundColor: isError ? 'rgba(255,0,0,0.05)' : 'rgba(0, 108, 156, 0.03)',
                                            boxShadow: isError ? '0 0 0 2px rgba(255,0,0,0.2)' : '0 0 0 2px rgba(51, 138, 243, 0.20)',
                                        }
                                    }}
                                >
                                    {params.value != null ? params.value : "--"}
                                </Box>
                            );
                        }

                    },

                    {
                        field: "max_cpc_target_reduction",
                        headerName: "Floor Max CPC",
                        type: "number",
                        editable: true,
                        flex: 1,
                        minWidth: 160,

                        valueGetter: (params: any) => {
                            const detail = params.row.activations_details?.find(
                                (d: any) => d.mode?.startsWith("KO Bidding Auto")
                            );
                            return detail?.max_cpc_target_reduction ?? null;
                        },

                        valueSetter: (params: any) => {
                            const { value, row } = params;

                            const updatedDetails = [...(row.activations_details || [])];

                            const index = updatedDetails.findIndex(
                                (d) => d.mode?.startsWith("KO Bidding Auto")
                            );

                            const numericValue =
                                value === "" || value === null || value === undefined
                                    ? null
                                    : Number(value);

                            if (numericValue !== null && numericValue < 0) {
                                return row;
                            }

                            if (index >= 0) {
                                updatedDetails[index] = {
                                    ...updatedDetails[index],
                                    max_cpc_target_reduction: numericValue,
                                };
                            } else {
                                updatedDetails.push({
                                    mode: "KO Bidding Auto",
                                    strategy: "Smart Top",
                                    max_cpc_target_reduction: numericValue,
                                });
                            }

                            return {
                                ...row,
                                activations_details: updatedDetails,
                            };
                        },

                        renderCell: (params: any) => {
                            const isError = params.value != null && params.value < 0;

                            const isRequiredError =
                                validationErrors?.[params.row.id]?.includes(
                                    "max_cpc_target_reduction"
                                );
                            return (
                                <Box
                                    onClick={() => {
                                        params.api.startCellEditMode({
                                            id: params.id,
                                            field: params.field,
                                        });
                                    }}
                                    sx={{
                                        borderRadius: "8px",
                                        border: `2px solid ${isError || isRequiredError
                                            ? "red"
                                            : "rgba(145, 158, 171, 0.16)"
                                            }`,
                                        display: "flex",

                                        alignItems: "center",
                                        width: "110px",
                                        height: "36px",
                                        overflow: "hidden",
                                        backgroundColor: isError ? "rgba(255,0,0,0.05)" : "transparent",
                                        "&:hover": {
                                            cursor: "pointer",
                                            borderColor: isError ? "red" : "rgba(0, 108, 156, 0.40)",
                                            backgroundColor: isError
                                                ? "rgba(255,0,0,0.05)"
                                                : "rgba(0, 108, 156, 0.03)",
                                            boxShadow: isError
                                                ? "0 0 0 2px rgba(255,0,0,0.2)"
                                                : "0 0 0 2px rgba(51, 138, 243, 0.20)",
                                        },
                                    }}
                                >
                                    <TextField
                                        value={params.value ?? ""}
                                        type="number"
                                        variant="standard"
                                        placeholder="--"
                                        onChange={(e: any) => {
                                            const val = e.target.value;

                                            if (val === "") {
                                                params.api.setEditCellValue({
                                                    id: params.id,
                                                    field: params.field,
                                                    value: "",
                                                });
                                                return;
                                            }

                                            const num = Number(val);

                                            if (num >= 0) {
                                                params.api.setEditCellValue({
                                                    id: params.id,
                                                    field: params.field,
                                                    value: num,
                                                });
                                            }
                                        }}
                                        inputProps={{
                                            min: 0,
                                            step: 0.01,
                                            style: {
                                                cursor: 'pointer',
                                                textAlign: "right",
                                                padding: "6px 8px",
                                                fontSize: "14px",
                                            },
                                        }}
                                        sx={{
                                            width: "100%",
                                            "& .MuiInput-root:before": { display: "none" },
                                            "& .MuiInput-root:after": { display: "none" },
                                        }}
                                    />
                                </Box>
                            );
                        }
                    },
                    {
                        field: "max_cpc_portfolio_strategy",
                        headerName: "Max CPC",
                        type: "number",
                        editable: true,
                        maxWidth: 180,
                        flex: 1,
                        renderCell: (params: any) => {
                            const rules = getKoBiddingRules(params.row);
                            const isError =
                                params.value != null &&
                                params.value < rules.minCPC;

                            const isRequiredError =
                                validationErrors?.[params.row.id]?.includes(
                                    "max_cpc_portfolio_strategy"
                                );
                            return (
                                <Box
                                    onClick={() => {
                                        params.api.startCellEditMode({
                                            id: params.id,
                                            field: params.field,
                                        });
                                    }}
                                    sx={{
                                        borderRadius: "8px",
                                        border: `2px solid ${isError || isRequiredError
                                            ? "red"
                                            : "rgba(145, 158, 171, 0.16)"
                                            }`,
                                        display: "flex",
                                        alignItems: "center",
                                        width: "90px",
                                        height: "36px",
                                        overflow: "hidden",
                                        backgroundColor: isError
                                            ? "rgba(255,0,0,0.05)"
                                            : "transparent",
                                        "&:hover": {
                                            cursor: "pointer",
                                            borderColor: isError
                                                ? "red"
                                                : "rgba(0, 108, 156, 0.40)",
                                            backgroundColor: isError
                                                ? "rgba(255,0,0,0.05)"
                                                : "rgba(0, 108, 156, 0.03)",
                                            boxShadow: isError
                                                ? "0 0 0 2px rgba(255,0,0,0.2)"
                                                : "0 0 0 2px rgba(51, 138, 243, 0.20)",
                                        },
                                    }}
                                >
                                    <TextField
                                        value={params.value ?? ""}
                                        type="number"
                                        variant="standard"
                                        placeholder="--"
                                        onChange={(e: any) => {
                                            const val = e.target.value;

                                            if (val === "") {
                                                params.api.setEditCellValue({
                                                    id: params.id,
                                                    field: params.field,
                                                    value: "",
                                                });
                                                return;
                                            }

                                            const num = Number(val);

                                            if (num >= rules.minCPC) {
                                                params.api.setEditCellValue({
                                                    id: params.id,
                                                    field: params.field,
                                                    value: num,
                                                });
                                            }
                                        }}
                                        inputProps={{
                                            min: 0,
                                            step: 0.01,
                                            style: {
                                                textAlign: "right",
                                                padding: "6px 8px",
                                                fontSize: "14px",
                                            },
                                        }}
                                        sx={{
                                            width: "100%",
                                            "& .MuiInput-root:before": {
                                                display: "none",
                                            },
                                            "& .MuiInput-root:after": {
                                                display: "none",
                                            },
                                        }}
                                    />

                                    <Box
                                        sx={{
                                            pr: 1,
                                            fontSize: 12,
                                            color: "rgba(99,115,129,1)",
                                        }}
                                    >
                                        {getCurrencySymbol(
                                            workspace?.currency,
                                            lang
                                        )}
                                    </Box>
                                </Box>
                            );
                        },
                    }
                ]
                : []),
    ];

    return (
        <Box sx={{ border: '1px solid rgba(145, 158, 171, 0.20)', borderRadius: "12px" }}>
            <DataGrid
                rows={rows}
                columns={columns}
                disableRowSelectionOnClick
                hideFooter
                getRowId={(r) => r.id ?? `${r.keyword}-${r.country}`}
                processRowUpdate={(newRow) => {
                    const updatedRows = rows.map((r) =>
                        r.id === newRow.id ? newRow : r
                    );
                    onChange?.(updatedRows);
                    return newRow;
                }}

                sx={{
                    color: "#FFF",
                    border: "none",
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: "#FAFBFB",
                        borderRadius: "12px",
                        color: "rgba(99, 115, 129, 1)",
                        fontWeight: 500,
                    },
                    "& .MuiDataGrid-cell": {
                        borderBottom: "0px",
                    },
                }}
            />
        </Box>
    );
}
